import { IInboxConversation } from "../models/messagesInbox/IInboxConversation";
import { decodeHtml } from "./decodeHtml";
import { detectRegs } from "./detectRegs";

/**
 * Words a buyer uses to say thanks, OK or goodbye. A message made only of these (plus
 * punctuation and emoji) doesn't need a reply. Anything else - a question, a number, a reg,
 * or one other word like "but" or "wrong" - and it stays in Needs reply.
 */
const ACKNOWLEDGEMENT_WORDS = new Set(
  (
    "thanks thank thankyou thanx thx ty ta cheers you u very much so many lots again " +
    "mate pal bud buddy man guys all " +
    "ok okay okey k kk fine cool sound ace great brilliant brill perfect lovely nice superb legend top " +
    "good stuff appreciated appreciate it that's thats " +
    "got received arrived item part safely today fab " +
    "bye cya see ya later have a day weekend " +
    "no worries problem np will do sorted"
  ).split(" ")
);

/** Up to this many words; longer messages usually say something that needs reading. */
const MAX_WORDS = 10;

/** True when a buyer's message is just thanks / OK / bye and needs no reply. */
export function isAcknowledgement(text: string) {
  const plain = decodeHtml(text).trim();
  if (!plain || plain.includes("?") || /\d/.test(plain) || detectRegs(plain).length > 0) return false;

  const words = plain
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[^a-z' ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  // Emoji or punctuation only, e.g. a thumbs up.
  if (words.length === 0) return /\p{Extended_Pictographic}/u.test(plain);
  // "no" on its own may be answering our question, so it needs reading.
  if (words.length === 1 && words[0] === "no") return false;
  return words.length <= MAX_WORDS && words.every((w) => ACKNOWLEDGEMENT_WORDS.has(w));
}

/**
 * Buyer messages since our last reply (or all of them if we've never replied). "No reply
 * needed" warns when there's more than one, so an earlier question isn't missed.
 */
export function unansweredBuyerMessages(c: IInboxConversation) {
  const lastOurs = c.messages.map((m) => m.direction).lastIndexOf("us");
  const lastAuto = c.messages.map((m) => m.direction).lastIndexOf("auto");
  return c.messages.slice(Math.max(lastOurs, lastAuto) + 1).filter((m) => m.direction === "buyer");
}

/**
 * Suggests "No reply needed" (it never marks it automatically): the buyer's latest message just
 * acknowledges ours - we replied, then they said thanks / OK / bye. Returns that message.
 */
export function noReplyNeededMessage(c: IInboxConversation) {
  const talk = c.messages.filter((m) => m.direction === "buyer" || m.direction === "us" || m.direction === "auto");
  const last = talk[talk.length - 1];
  const before = talk[talk.length - 2];
  if (!last || last.direction !== "buyer" || !before || before.direction === "buyer") return undefined;
  return isAcknowledgement(last.body) ? last : undefined;
}
